import type {
  ActivateRequest,
  ActivateResponse,
  AuthProvidersResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginCodeRequestRequest,
  LoginCodeRequestResponse,
  LoginCodeVerifyRequest,
  LoginCodeVerifyResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  RegisterRequest,
  RegisterResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SessionResponse,
  VerifyEmailResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/auth/*`.
 *
 * These are plain imperative calls — the session probe itself is
 * driven by `<SessionProvider>` rather than a React Query hook (we want
 * deterministic ordering and a non-cached status for the guard). Form
 * components own their own loading / error state.
 */

export async function fetchSession(fetchImpl: typeof fetch): Promise<SessionResponse> {
  return platformApiRequest<SessionResponse>('/v1/auth/session', {}, fetchImpl);
}

export async function login(
  body: LoginRequest,
  fetchImpl: typeof fetch,
): Promise<LoginResponse> {
  return platformApiRequest<LoginResponse>(
    '/v1/auth/login',
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function logout(fetchImpl: typeof fetch): Promise<LogoutResponse> {
  return platformApiRequest<LogoutResponse>(
    '/v1/auth/logout',
    { method: 'POST' },
    fetchImpl,
  );
}

export async function register(
  body: RegisterRequest,
  fetchImpl: typeof fetch,
): Promise<RegisterResponse> {
  return platformApiRequest<RegisterResponse>(
    '/v1/auth/register',
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Call `POST /v1/auth/verify-email` with the token from the email link.
 *
 * The POST variant mints a session cookie and returns the principal in
 * one round-trip — the verify-email page uses that to redirect straight
 * into `/dashboard` without a second sign-in. The legacy `GET` variant
 * is intentionally not used from the console any more (it stays wired on
 * the backend for pre-existing email links until the deploy window).
 */
export async function verifyEmail(
  token: string,
  fetchImpl: typeof fetch,
): Promise<VerifyEmailResponse> {
  return platformApiRequest<VerifyEmailResponse>(
    '/v1/auth/verify-email',
    { method: 'POST', body: { token } },
    fetchImpl,
  );
}

/**
 * Request a fresh verification email. Always returns `{ ok: true }` from
 * the backend — there is no enumeration oracle, so the console cannot
 * tell the caller whether the email matched an unverified account. The
 * UI surfaces a generic "check your inbox" acknowledgement regardless.
 */
export async function resendVerificationEmail(
  body: ResendVerificationRequest,
  fetchImpl: typeof fetch,
): Promise<ResendVerificationResponse> {
  return platformApiRequest<ResendVerificationResponse>(
    '/v1/auth/verify-email/resend',
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function forgotPassword(
  body: ForgotPasswordRequest,
  fetchImpl: typeof fetch,
): Promise<ForgotPasswordResponse> {
  return platformApiRequest<ForgotPasswordResponse>(
    '/v1/auth/password/forgot',
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function resetPassword(
  body: ResetPasswordRequest,
  fetchImpl: typeof fetch,
): Promise<ResetPasswordResponse> {
  return platformApiRequest<ResetPasswordResponse>(
    '/v1/auth/password/reset',
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function changePassword(
  body: ChangePasswordRequest,
  fetchImpl: typeof fetch,
): Promise<ChangePasswordResponse> {
  return platformApiRequest<ChangePasswordResponse>(
    '/v1/auth/password/change',
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function fetchProviders(
  fetchImpl: typeof fetch,
): Promise<AuthProvidersResponse> {
  return platformApiRequest<AuthProvidersResponse>('/v1/auth/providers', {}, fetchImpl);
}

/**
 * Invite-code activation (epic #1475). One round-trip: account, org,
 * customer-manager binding, trial credit, and the session cookie. The
 * returned grant is a snapshot taken at issuance — render it verbatim
 * rather than recomputing the amount or expiry from current settings.
 */
export async function activate(
  body: ActivateRequest,
  fetchImpl: typeof fetch,
): Promise<ActivateResponse> {
  return platformApiRequest<ActivateResponse>(
    '/v1/auth/activate',
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Ask for an email sign-in code. Always resolves 202 with the same shape
 * whether or not the address is registered, so the UI must acknowledge
 * generically — see `LoginCodeForm`.
 */
export async function requestLoginCode(
  body: LoginCodeRequestRequest,
  fetchImpl: typeof fetch,
): Promise<LoginCodeRequestResponse> {
  return platformApiRequest<LoginCodeRequestResponse>(
    '/v1/auth/login/code/request',
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Exchange a sign-in code for a session cookie. This is the only step that
 * discloses account status (suspended / closed), and it may: the caller has
 * proven they hold the mailbox by getting the code right.
 */
export async function verifyLoginCode(
  body: LoginCodeVerifyRequest,
  fetchImpl: typeof fetch,
): Promise<LoginCodeVerifyResponse> {
  return platformApiRequest<LoginCodeVerifyResponse>(
    '/v1/auth/login/code/verify',
    { method: 'POST', body },
    fetchImpl,
  );
}
